import AnimatedContent from "@/components/animated-content";
import { Button } from "@/components/button";
import { ContactSection } from "@/components/contact-section";
import FloatingLines from "@/components/FloatingLines";
import RotatingText from "@/components/rotating-text";
import ShinyText from "@/components/shiny-text";
import SplitText from "@/components/split-text";
import StarBorder from "@/components/star-border";
import { AppLayout } from "@/layouts/app/app-layout";
import { trackCta, trackService } from "@/lib/analytics";
import { useEffect, useState } from "react";

// Titles double as the analytics property, so the dashboard reads the same way
// the page does.
const SERVICE_NAMES: Record<number, string> = {
  1: "Software-Entwicklung",
  2: "App-Entwicklung",
  3: "Prozessoptimierung",
  4: "Corporate Design",
  5: "EDV Dienstleistung",
  6: "Webentwicklung",
};

// Running text questions array
const runningTextQuestions = [
  "Heften Sie Ihre Rechnungen noch ab?",
  "Machen Sie Termine noch am Telefon?",
  "Suchen Sie noch nach Dokumenten?",
  "Schreiben Sie Angebote noch per Hand?",
  "Führen Sie Listen noch in Excel?",
  "Versenden Sie Mahnungen noch per Post?",
  "Bearbeiten Sie Bestellungen noch manuell?",
  "Verwalten Sie Kunden noch in Karteikarten?",
  "Drucken Sie Berichte noch aus?",
  "Übertragen Sie Daten noch doppelt?",
  "Kalkulieren Sie Preise noch im Kopf?",
  "Archivieren Sie noch in Ordnern?",
  "Erfassen Sie Arbeitszeiten noch auf Papier?",
  "Planen Sie Projekte noch am Whiteboard?",
  "Verfolgen Sie Lagerbestände noch manuell?",
];

export default function Home() {
  const [expandedService, setExpandedService] = useState<number | null>(null);
  const [scrollY, setScrollY] = useState(0);

  const toggleService = (service: number) => {
    const next = expandedService === service ? null : service;
    setExpandedService(next);
    trackService(SERVICE_NAMES[service], next !== null);
  };

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <AppLayout>
      <section className="relative min-h-screen w-screen overflow-hidden">
        <div className="absolute inset-0">
          <FloatingLines
            enabledWaves={["top", "middle", "bottom"]}
            lineCount={4}
            lineDistance={5}
            bendRadius={1}
            bendStrength={-9.5}
            interactive={true}
            parallax={true}
          />
        </div>

        <div className="relative z-10 container mx-auto flex min-h-screen flex-col px-4 pt-20 sm:pt-24">
          {/* Main Content */}
          <div className="mx-auto flex max-w-6xl flex-1 flex-col items-center justify-center text-center">
            {/* Header Badge */}
            <div className="mb-6 sm:mb-8">
              <Button className="cursor-default border border-purple-500/30 bg-purple-900/50 backdrop-blur-sm">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 animate-pulse rounded-full bg-green-400"></div>
                  <span className="text-sm text-purple-200 sm:text-base">
                    <span className="hidden sm:inline">
                      Digitale Transformation • Made in Hessen
                    </span>
                    <span className="sm:hidden">Digital • Made in Hessen</span>
                  </span>
                </div>
              </Button>
            </div>

            {/* Main Headline */}
            <div className="mb-8 sm:mb-12">
              <h1 className="text-4xl leading-tight font-black tracking-tight text-white sm:text-5xl md:text-6xl lg:text-7xl">
                <SplitText
                  text="Sind Sie schon"
                  delay={50}
                  duration={0.8}
                  ease="power2.out"
                  from={{ opacity: 0, y: 60 }}
                  to={{ opacity: 1, y: 0 }}
                  splitType="words"
                  threshold={0}
                  rootMargin="0px"
                />
                <br />
                <span className="-mt-6 inline-block sm:-mt-8">
                  <RotatingText
                    texts={[
                      "digital?",
                      "effizient?",
                      "zukunftssicher?",
                      "profitabel?",
                      "automatisiert?",
                      "vernetzt?",
                    ]}
                    mainClassName=""
                    elementLevelClassName="relative z-10 text-purple-400"
                    staggerFrom="center"
                    initial={{ y: "100%" }}
                    animate={{ y: 0 }}
                    exit={{ y: "-120%" }}
                    staggerDuration={0.03}
                    splitLevelClassName="overflow-hidden"
                    transition={{
                      type: "spring",
                      damping: 30,
                      stiffness: 400,
                      duration: 0.8,
                    }}
                    rotationInterval={2500}
                  />
                </span>
              </h1>
            </div>

            {/* Subheadline */}
            <div className="mb-16 max-w-2xl">
              <AnimatedContent
                distance={30}
                direction="vertical"
                duration={0.8}
                delay={0.6}
                threshold={0}
              >
                <p className="text-xl leading-relaxed text-gray-300">
                  Maßgeschneiderte Digitallösungen für Ihren Erfolg – von der
                  Idee bis zur Umsetzung.
                </p>
              </AnimatedContent>
            </div>

            {/* CTA Buttons */}
            <div className="mb-20 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <StarBorder
                as="a"
                href="#kontakt"
                onClick={() => trackCta("Kontakt aufnehmen", "hero")}
                className="sm:w-auto"
                color="rgb(192, 132, 252)"
                speed="4s"
              >
                <div className="flex items-center justify-center gap-2 px-6">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="h-4 w-4"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                    />
                  </svg>
                  <span className="font-medium">Kontakt aufnehmen</span>
                </div>
              </StarBorder>
            </div>

            {/* Trust Indicators */}
            <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-center gap-8 text-xs text-gray-400">
              <div className="flex items-center gap-2">
                <svg
                  className="h-3 w-3 text-green-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>100% maßgeschneidert</span>
              </div>
              <div className="flex items-center gap-2">
                <svg
                  className="h-3 w-3 text-green-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>20+ Jahre Erfahrung</span>
              </div>
              <div className="flex items-center gap-2">
                <svg
                  className="h-3 w-3 text-green-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Persönliche Betreuung</span>
              </div>
            </div>
          </div>

          {/* Scroll Indicator */}
          <div
            className="flex justify-center pb-8 transition-opacity duration-500"
            style={{ opacity: Math.max(0, 1 - scrollY / 300) }}
          >
            <div className="animate-bounce">
              <svg
                className="h-6 w-6 text-purple-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 14l-7 7m0 0l-7-7m7 7V3"
                />
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* Running Text Section */}
      <section className="w-screen overflow-hidden bg-black py-16">
        <div className="relative">
          {/* Left fade */}
          <div className="absolute top-0 left-0 z-10 h-full w-56 bg-gradient-to-r from-black to-transparent"></div>
          {/* Right fade */}
          <div className="absolute top-0 right-0 z-10 h-full w-56 bg-gradient-to-l from-black to-transparent"></div>

          {/* Running text container with manual scroll capability */}
          <div
            className="flex whitespace-nowrap"
            style={{
              animation: "scroll 10s linear infinite",
              animationPlayState: "running",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.animationPlayState = "paused")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.animationPlayState = "running")
            }
            onTouchStart={(e) =>
              (e.currentTarget.style.animationPlayState = "paused")
            }
            onTouchEnd={(e) =>
              (e.currentTarget.style.animationPlayState = "running")
            }
          >
            {/* First set of questions */}
            <div className="rounded-4 flex items-center space-x-8">
              {runningTextQuestions.map((question, index) => (
                <span
                  key={index}
                  className="group cursor-pointer rounded-lg px-4 py-2 text-white/80 transition-all duration-300 hover:text-white"
                >
                  {question}
                </span>
              ))}
            </div>

            {/* Duplicate for seamless loop */}
            <div className="ml-8 flex items-center space-x-8">
              {runningTextQuestions.map((question, index) => (
                <span
                  key={`duplicate-${index}`}
                  className="group cursor-pointer rounded-lg px-4 py-2 text-white/80 transition-all duration-300 hover:text-white"
                >
                  {question}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services-section" className="bg-zinc-950 py-24">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-6xl">
            <div className="mb-12">
              <h1 className="mb-4 text-5xl font-black tracking-tight">
                <SplitText
                  text="Unsere Leistungen"
                  delay={30}
                  duration={0.6}
                  ease="power2.out"
                  splitType="words"
                />
              </h1>
              <AnimatedContent
                distance={30}
                direction="vertical"
                duration={0.8}
                delay={0.3}
                threshold={0.2}
              >
                <p className="text-xl text-gray-400">
                  Maßgeschneiderte digitale Lösungen, die Ihr Unternehmen{" "}
                  <ShinyText text="auf die nächste Stufe" /> heben.
                </p>
              </AnimatedContent>
            </div>
            <div className="grid grid-cols-1 gap-6 transition-all duration-500 ease-in-out md:grid-cols-2 lg:grid-cols-3">
              {/* Service 1 - Software Development */}
              <AnimatedContent
                distance={60}
                direction="vertical"
                duration={0.8}
                delay={0.1}
                threshold={0.1}
              >
                <div
                  onClick={() => toggleService(1)}
                  className={`group cursor-pointer overflow-hidden rounded-3xl p-8 shadow-sm transition-all duration-500 ease-in-out hover:scale-105 ${
                    expandedService === 1
                      ? "scale-110 transform bg-gradient-to-br from-purple-600 to-purple-700 text-white"
                      : "scale-100 transform border border-white/10 bg-zinc-900 text-white hover:border-purple-500/40 hover:shadow-lg hover:shadow-purple-500/10"
                  }`}
                  style={{
                    transformOrigin: "center",
                    minHeight: expandedService === 1 ? "400px" : "320px",
                    transition:
                      "all 0.5s ease-in-out, min-height 0.5s ease-in-out",
                  }}
                >
                  <div className="flex h-full flex-col">
                    <div className="mb-4">
                      <svg
                        className={`h-8 w-8 transition-colors duration-300 ${expandedService === 1 ? "text-white" : "text-purple-400/70"}`}
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect width="20" height="16" x="2" y="4" rx="2" />
                        <path d="M6 8h.01" />
                        <path d="M10 8h.01" />
                        <path d="M14 8h.01" />
                      </svg>
                    </div>
                    <h2 className="mb-4 text-2xl font-bold">
                      Software-Entwicklung
                    </h2>
                    <div className="mb-8 flex-1">
                      <p
                        className={`transition-opacity duration-300 ${expandedService === 1 ? "text-white/90" : "text-gray-400"}`}
                      >
                        {expandedService === 1
                          ? "Kein Unternehmen ist wie das andere – deshalb entwickeln wir Software, die zu Ihnen passt. Unsere Anwendungen bilden Ihre spezifischen Abläufe präzise ab und schaffen Klarheit, Automatisierung und Effizienz. Ob kleine Optimierung oder komplexe Individualentwicklung: Wir bauen Lösungen, die sich nahtlos in Ihre Prozesse integrieren und echten Mehrwert liefern."
                          : "Maßgeschneiderte Softwarelösungen, die Ihre Geschäftsprozesse optimieren und digitalisieren."}
                      </p>
                    </div>
                    <button
                      className={`mt-auto inline-flex items-center text-sm font-semibold transition-colors duration-300 ${
                        expandedService === 1 ? "text-white" : "text-purple-300"
                      }`}
                    >
                      {expandedService === 1
                        ? "Weniger anzeigen"
                        : "Mehr erfahren"}
                      <svg
                        className="ml-2 h-4 w-4"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M5 12h14M12 5l7 7-7 7"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </AnimatedContent>

              {/* Service 2 - App Development */}
              <AnimatedContent
                distance={60}
                direction="vertical"
                duration={0.8}
                delay={0.2}
                threshold={0.1}
              >
                <div
                  onClick={() => toggleService(2)}
                  className={`group cursor-pointer overflow-hidden rounded-3xl p-8 shadow-sm transition-all duration-500 ease-in-out hover:scale-105 ${
                    expandedService === 2
                      ? "scale-110 transform bg-gradient-to-br from-blue-600 to-blue-700 text-white"
                      : "scale-100 transform border border-white/10 bg-zinc-900 text-white hover:border-purple-500/40 hover:shadow-lg hover:shadow-purple-500/10"
                  }`}
                  style={{
                    transformOrigin: "center",
                    minHeight: expandedService === 2 ? "400px" : "320px",
                    transition:
                      "all 0.5s ease-in-out, min-height 0.5s ease-in-out",
                  }}
                >
                  <div className="flex h-full flex-col">
                    <div className="mb-4">
                      <svg
                        className={`h-8 w-8 transition-colors duration-300 ${expandedService === 2 ? "text-white" : "text-purple-400/70"}`}
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect
                          width="14"
                          height="20"
                          x="5"
                          y="2"
                          rx="2"
                          ry="2"
                        />
                        <path d="M12 18h.01" />
                      </svg>
                    </div>
                    <h2 className="mb-4 text-2xl font-bold">App-Entwicklung</h2>
                    <div className="mb-8 flex-1">
                      <p
                        className={`transition-opacity duration-300 ${expandedService === 2 ? "text-white/90" : "text-gray-400"}`}
                      >
                        {expandedService === 2
                          ? "Wir entwickeln benutzerfreundliche und leistungsstarke mobile Anwendungen für iOS und Android. Unsere Apps sind nicht nur funktional, sondern auch ansprechend gestaltet, um das Nutzererlebnis zu maximieren. Von der Konzeption bis zur Veröffentlichung im App Store begleiten wir Sie durch den gesamten Entwicklungsprozess."
                          : "Benutzerfreundliche und leistungsstarke mobile Anwendungen für iOS und Android."}
                      </p>
                    </div>
                    <button
                      className={`mt-auto inline-flex items-center text-sm font-semibold transition-colors duration-300 ${
                        expandedService === 2 ? "text-white" : "text-purple-300"
                      }`}
                    >
                      {expandedService === 2
                        ? "Weniger anzeigen"
                        : "Mehr erfahren"}
                      <svg
                        className="ml-2 h-4 w-4"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M5 12h14M12 5l7 7-7 7"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </AnimatedContent>

              {/* Service 3 - Process Optimization */}
              <AnimatedContent
                distance={60}
                direction="vertical"
                duration={0.8}
                delay={0.3}
                threshold={0.1}
              >
                <div
                  onClick={() => toggleService(3)}
                  className={`group cursor-pointer overflow-hidden rounded-3xl p-8 shadow-sm transition-all duration-500 ease-in-out hover:scale-105 ${
                    expandedService === 3
                      ? "scale-110 transform bg-gradient-to-br from-green-600 to-green-700 text-white"
                      : "scale-100 transform border border-white/10 bg-zinc-900 text-white hover:border-purple-500/40 hover:shadow-lg hover:shadow-purple-500/10"
                  }`}
                  style={{
                    transformOrigin: "center",
                    minHeight: expandedService === 3 ? "400px" : "320px",
                    transition:
                      "all 0.5s ease-in-out, min-height 0.5s ease-in-out",
                  }}
                >
                  <div className="flex h-full flex-col">
                    <div className="mb-4">
                      <svg
                        className={`h-8 w-8 transition-colors duration-300 ${expandedService === 3 ? "text-white" : "text-purple-400/70"}`}
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M16 7h6v6" />
                        <path d="m22 7-8.5 8.5-5-5L2 17" />
                      </svg>
                    </div>
                    <h2 className="mb-4 text-2xl font-bold">
                      Prozessoptimierung
                    </h2>
                    <div className="mb-8 flex-1">
                      <p
                        className={`transition-opacity duration-300 ${expandedService === 3 ? "text-white/90" : "text-gray-400"}`}
                      >
                        {expandedService === 3
                          ? "Optimierte Prozesse sind der Schlüssel zu mehr Effizienz und Wettbewerbsfähigkeit. Mit Erfahrung aus verschiedenen Branchen kennen wir die Praxis und wissen, wie man sie besser macht. Gemeinsam analysieren wir Ihre Ist-Prozesse, identifizieren Schwachstellen und schaffen automatisierte Workflows, die entlasten statt belasten."
                          : "Effiziente Prozessoptimierung für mehr Produktivität und Wettbewerbsfähigkeit."}
                      </p>
                    </div>
                    <button
                      className={`mt-auto inline-flex items-center text-sm font-semibold transition-colors duration-300 ${
                        expandedService === 3 ? "text-white" : "text-purple-300"
                      }`}
                    >
                      {expandedService === 3
                        ? "Weniger anzeigen"
                        : "Mehr erfahren"}
                      <svg
                        className="ml-2 h-4 w-4"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M5 12h14M12 5l7 7-7 7"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </AnimatedContent>

              {/* Service 4 - Corporate Design */}
              <AnimatedContent
                distance={60}
                direction="vertical"
                duration={0.8}
                delay={0.4}
                threshold={0.1}
              >
                <div
                  onClick={() => toggleService(4)}
                  className={`group cursor-pointer overflow-hidden rounded-3xl p-8 shadow-sm transition-all duration-500 ease-in-out hover:scale-105 ${
                    expandedService === 4
                      ? "scale-110 transform bg-gradient-to-br from-orange-600 to-orange-700 text-white"
                      : "scale-100 transform border border-white/10 bg-zinc-900 text-white hover:border-purple-500/40 hover:shadow-lg hover:shadow-purple-500/10"
                  }`}
                  style={{
                    transformOrigin: "center",
                    minHeight: expandedService === 4 ? "400px" : "320px",
                    transition:
                      "all 0.5s ease-in-out, min-height 0.5s ease-in-out",
                  }}
                >
                  <div className="flex h-full flex-col">
                    <div className="mb-4">
                      <svg
                        className={`h-8 w-8 transition-colors duration-300 ${expandedService === 4 ? "text-white" : "text-purple-400/70"}`}
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M12 20h9" />
                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                      </svg>
                    </div>
                    <h2 className="mb-4 text-2xl font-bold">
                      Corporate Design
                    </h2>
                    <div className="mb-8 flex-1">
                      <p
                        className={`transition-opacity duration-300 ${expandedService === 4 ? "text-white/90" : "text-gray-400"}`}
                      >
                        {expandedService === 4
                          ? "Ihr visuelles Erscheinungsbild ist mehr als nur ein Logo – es ist Ihr Markenfundament. Wir entwickeln ein ganzheitliches Corporate Design, das Werte sichtbar macht und Ihre Position im Markt stärkt. Vom Signet bis zum Farbkonzept, von der Visitenkarte bis zum Designsystem: Wir sorgen für einen konsistenten Markenauftritt über alle Kanäle hinweg."
                          : "Ganzheitliches Corporate Design, das Ihre Marke stärkt und Ihre Werte sichtbar macht."}
                      </p>
                    </div>
                    <button
                      className={`mt-auto inline-flex items-center text-sm font-semibold transition-colors duration-300 ${
                        expandedService === 4 ? "text-white" : "text-purple-300"
                      }`}
                    >
                      {expandedService === 4
                        ? "Weniger anzeigen"
                        : "Mehr erfahren"}
                      <svg
                        className="ml-2 h-4 w-4"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M5 12h14M12 5l7 7-7 7"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </AnimatedContent>

              {/* Service 5 - EDV Dienstleistung */}
              <AnimatedContent
                distance={60}
                direction="vertical"
                duration={0.8}
                delay={0.5}
                threshold={0.1}
              >
                <div
                  onClick={() => toggleService(5)}
                  className={`group cursor-pointer overflow-hidden rounded-3xl p-8 shadow-sm transition-all duration-500 ease-in-out hover:scale-105 ${
                    expandedService === 5
                      ? "scale-110 transform bg-gradient-to-br from-indigo-600 to-indigo-700 text-white"
                      : "scale-100 transform border border-white/10 bg-zinc-900 text-white hover:border-purple-500/40 hover:shadow-lg hover:shadow-purple-500/10"
                  }`}
                  style={{
                    transformOrigin: "center",
                    minHeight: expandedService === 5 ? "400px" : "320px",
                    transition:
                      "all 0.5s ease-in-out, min-height 0.5s ease-in-out",
                  }}
                >
                  <div className="flex h-full flex-col">
                    <div className="mb-4">
                      <svg
                        className={`h-8 w-8 transition-colors duration-300 ${expandedService === 5 ? "text-white" : "text-purple-400/70"}`}
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M20 7h-9" />
                        <path d="M14 17H5" />
                        <circle cx="17" cy="17" r="3" />
                        <circle cx="7" cy="7" r="3" />
                      </svg>
                    </div>
                    <h2 className="mb-4 text-2xl font-bold">
                      EDV Dienstleistung
                    </h2>
                    <div className="mb-8 flex-1">
                      <p
                        className={`transition-opacity duration-300 ${expandedService === 5 ? "text-white/90" : "text-gray-400"}`}
                      >
                        {expandedService === 5
                          ? "Von der ersten Beratung bis zur laufenden Wartung – wir sind Ihr zuverlässiger IT-Partner. Ob Arztpraxis, Kanzlei oder Mittelstandsunternehmen: Wir kümmern uns um Ihre komplette IT-Infrastruktur. Netzwerk-Setup, Server-Wartung, Software-Updates und Notfall-Support – damit Sie sich auf Ihr Kerngeschäft konzentrieren können."
                          : "Umfassende IT-Betreuung für Unternehmen jeder Größe – von Beratung bis Wartung."}
                      </p>
                    </div>
                    <button
                      className={`mt-auto inline-flex items-center text-sm font-semibold transition-colors duration-300 ${
                        expandedService === 5 ? "text-white" : "text-purple-300"
                      }`}
                    >
                      {expandedService === 5
                        ? "Weniger anzeigen"
                        : "Mehr erfahren"}
                      <svg
                        className="ml-2 h-4 w-4"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M5 12h14M12 5l7 7-7 7"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </AnimatedContent>

              {/* Service 6 - Webentwicklung */}
              <AnimatedContent
                distance={60}
                direction="vertical"
                duration={0.8}
                delay={0.6}
                threshold={0.1}
              >
                <div
                  onClick={() => toggleService(6)}
                  className={`group cursor-pointer overflow-hidden rounded-3xl p-8 shadow-sm transition-all duration-500 ease-in-out hover:scale-105 ${
                    expandedService === 6
                      ? "scale-110 transform bg-gradient-to-br from-pink-600 to-pink-700 text-white"
                      : "scale-100 transform border border-white/10 bg-zinc-900 text-white hover:border-purple-500/40 hover:shadow-lg hover:shadow-purple-500/10"
                  }`}
                  style={{
                    transformOrigin: "center",
                    minHeight: expandedService === 6 ? "400px" : "320px",
                    transition:
                      "all 0.5s ease-in-out, min-height 0.5s ease-in-out",
                  }}
                >
                  <div className="flex h-full flex-col">
                    <div className="mb-4">
                      <svg
                        className={`h-8 w-8 transition-colors duration-300 ${expandedService === 6 ? "text-white" : "text-purple-400/70"}`}
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <path d="M8 12h8" />
                        <path d="M12 8v8" />
                      </svg>
                    </div>
                    <h2 className="mb-4 text-2xl font-bold">Webentwicklung</h2>
                    <div className="mb-8 flex-1">
                      <p
                        className={`transition-opacity duration-300 ${expandedService === 6 ? "text-white/90" : "text-gray-400"}`}
                      >
                        {expandedService === 6
                          ? "Moderne Websites und Webanwendungen, die überzeugen und konvertieren. Von der einfachen Unternehmenswebsite bis zur komplexen Web-App entwickeln wir responsive, performante und benutzerfreundliche Lösungen. SEO-optimiert, DSGVO-konform und mit modernsten Technologien – für einen professionellen Auftritt im digitalen Raum."
                          : "Moderne, responsive Websites und Webanwendungen für einen starken digitalen Auftritt."}
                      </p>
                    </div>
                    <button
                      className={`mt-auto inline-flex items-center text-sm font-semibold transition-colors duration-300 ${
                        expandedService === 6 ? "text-white" : "text-purple-300"
                      }`}
                    >
                      {expandedService === 6
                        ? "Weniger anzeigen"
                        : "Mehr erfahren"}
                      <svg
                        className="ml-2 h-4 w-4"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M5 12h14M12 5l7 7-7 7"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </AnimatedContent>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="w-screen bg-black py-24">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="mb-8 text-4xl font-bold text-white">
              <SplitText
                text="Über uns"
                delay={35}
                duration={0.6}
                ease="power2.out"
                splitType="words"
              />
            </h2>
            <div className="space-y-6">
              <AnimatedContent
                distance={30}
                direction="vertical"
                duration={0.8}
                delay={0.2}
                threshold={0.2}
              >
                <p className="text-xl leading-relaxed text-gray-400">
                  <strong className="text-white">Harmonie Media</strong> ist
                  Ihr Partner für digitale Transformation. Wir entwickeln
                  Software, die Ihre Geschäftsprozesse optimiert und Ihr
                  Unternehmen zukunftssicher macht.
                </p>
              </AnimatedContent>
              <AnimatedContent
                distance={30}
                direction="vertical"
                duration={0.8}
                delay={0.4}
                threshold={0.2}
              >
                <p className="text-lg leading-relaxed text-gray-400">
                  Mit über 20 Jahren Erfahrung analysieren wir bestehende
                  Abläufe, identifizieren Verbesserungspotenziale und entwickeln
                  passgenaue Lösungen, die Zeit sparen und Ressourcen schonen.
                </p>
              </AnimatedContent>
            </div>

            <div className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-3">
              <AnimatedContent
                distance={60}
                direction="vertical"
                duration={0.8}
                delay={0.1}
                threshold={0.2}
              >
                <div className="text-center">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border border-purple-500/30 bg-purple-500/15">
                    <svg
                      className="h-8 w-8 text-purple-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                      />
                    </svg>
                  </div>
                  <h3 className="mb-2 text-lg font-semibold text-white">
                    Innovation
                  </h3>
                  <p className="text-gray-400">
                    Modernste Technologien für zukunftssichere Lösungen
                  </p>
                </div>
              </AnimatedContent>
              <AnimatedContent
                distance={60}
                direction="vertical"
                duration={0.8}
                delay={0.2}
                threshold={0.2}
              >
                <div className="text-center">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border border-purple-500/30 bg-purple-500/15">
                    <svg
                      className="h-8 w-8 text-purple-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                  </div>
                  <h3 className="mb-2 text-lg font-semibold text-white">
                    Partnerschaft
                  </h3>
                  <p className="text-gray-400">
                    Langfristige Zusammenarbeit auf Augenhöhe
                  </p>
                </div>
              </AnimatedContent>
              <AnimatedContent
                distance={60}
                direction="vertical"
                duration={0.8}
                delay={0.3}
                threshold={0.2}
              >
                <div className="text-center">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border border-purple-500/30 bg-purple-500/15">
                    <svg
                      className="h-8 w-8 text-purple-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                      />
                    </svg>
                  </div>
                  <h3 className="mb-2 text-lg font-semibold text-white">
                    Qualität
                  </h3>
                  <p className="text-gray-400">
                    Höchste Standards in Entwicklung und Service
                  </p>
                </div>
              </AnimatedContent>
            </div>
          </div>
        </div>
      </section>

      <ContactSection location="contact-section" />
    </AppLayout>
  );
}
